import styles from "@styles/CircularDesignMetrics.module.css";
import { Html } from "@react-three/drei";
import { HtmlProps } from "@react-three/drei/web/Html";
import { Color, ColorRepresentation, Matrix3, Vector2 } from "three";
import { MathUtils } from "@utils/MathUtils";
import { CSSProperties, useCallback, useMemo } from "react";
import { animated, SpringValue } from "react-spring";

type SegmentProps = {
    rotation: number;
    angle: number;
    size: number;
    totalSize: number;
    value: number | boolean;
    min?: number;
    max?: number;
    unit?: string;
    fill?: number;
    color: [from: ColorRepresentation, to: ColorRepresentation];
    margin?: number;
    label?: string;
    labelOffset?: number;
    labelStyle?: CSSProperties;
    drawText?: boolean;
}

const matrix = new Matrix3();
const Segment = animated(({ rotation, angle, size, totalSize, value, min = 0, max = 1, unit = "", color, margin = 0, label, labelOffset = 0, labelStyle, drawText = false }: SegmentProps) => {
    const [
        circleCenter, center,
        outerLeftCorner, outerRightCorner,
        innerLeftCorner, innerRightCorner,
        labelLeftCorner, labelRightCorner,
        textLeftCorner, textRightCorner,
        rightCorner
    ] = useMemo(() => [
        new Vector2(), new Vector2(),
        new Vector2(), new Vector2(),
        new Vector2(), new Vector2(),
        new Vector2(), new Vector2(),
        new Vector2(), new Vector2(),
        new Vector2()
    ], []);

    const fill = useMemo(() => {
        if (typeof value === "number") return MathUtils.mapRange(value, min, max, 0, 1);
        return value ? 1 : 0;
    }, [value, min, max]);

    const text = useMemo(() => {
        if (typeof value === "number") {
            if (unit === "%") {
                let v = value * 100;
                return v.toFixed(Number.isInteger(v) ? 0 : 1);
            }
            return value.toFixed(1);
        }
        return value ? "Yes" : "No";
    }, [value, unit]);

    const colorHex = useMemo(() => {
        const minColor = new Color(color[0]);
        const maxColor = new Color(color[1]);
        return new Color().lerpColors(minColor, maxColor, fill).getHexString();
    }, [color[0], color[1], fill]); // eslint-disable-line react-hooks/exhaustive-deps

    const radius = useMemo(() => {
        const radius = totalSize * 0.5;
        circleCenter.set(radius, radius);
        return radius;
    }, [totalSize, circleCenter]);

    const computePoints = useCallback((r: number, left: Vector2 | undefined, right: Vector2 | undefined) => {
        const m = margin * radius / r;
        if (left) {
            const al = rotation - angle * 0.5 + m;
            matrix.makeRotation(al * MathUtils.deg2Rad);
            left.set(0, r).applyMatrix3(matrix).add(circleCenter);
        }
        if (right) {
            const ar = rotation + angle * 0.5 - m;
            matrix.makeRotation(ar * MathUtils.deg2Rad);
            right.set(0, r).applyMatrix3(matrix).add(circleCenter);
        }
    }, [radius, margin, rotation, angle, circleCenter]);

    const innerRadius = useMemo(() => {
        const r = radius - size;
        computePoints(r, innerLeftCorner, innerRightCorner);
        return r;
    }, [radius, size, computePoints, innerLeftCorner, innerRightCorner]);

    const outerRadius = useMemo(() => {
        const r = MathUtils.lerp(innerRadius, radius, Math.max(fill, 0.05));
        computePoints(r, outerLeftCorner, outerRightCorner);
        return r;
    }, [radius, innerRadius, fill, computePoints, outerLeftCorner, outerRightCorner]);

    const labelRadius = useMemo(() => {
        const r = radius + labelOffset;
        computePoints(r, labelLeftCorner, labelRightCorner);
        return r;
    }, [radius, labelOffset, computePoints, labelLeftCorner, labelRightCorner]);

    const textRadius = useMemo(() => {
        const r = radius - size * 0.5;
        computePoints(r, textLeftCorner, textRightCorner);
        return r;
    }, [radius, size, computePoints, textLeftCorner, textRightCorner]);

    useMemo(() => {
        computePoints(radius, undefined, rightCorner);
    }, [radius, computePoints, rightCorner]);

    center.addVectors(innerLeftCorner, rightCorner).multiplyScalar(0.5);

    const [id, textId] = useMemo(() => {
        const id = label ? label.replaceAll(/\s/g, "-") : "";
        const textId = `${id}-value`;
        return [id, textId];
    }, [label]);

    const flip = useMemo(() => {
        let textRotation = rotation - 180;
        let flip = false;
        while (textRotation > 90) {
            textRotation -= 180;
            flip = !flip;
        }
        return flip;
    }, [rotation]);

    return (
        <g>
            <path d={`
                M ${outerLeftCorner.x} ${outerLeftCorner.y} 
                A ${outerRadius} ${outerRadius} 0 0 1 ${outerRightCorner.x} ${outerRightCorner.y} 
                L ${innerRightCorner.x} ${innerRightCorner.y} 
                A ${innerRadius} ${innerRadius} 0 0 0 ${innerLeftCorner.x} ${innerLeftCorner.y}
            `} style={{ fill: `#${colorHex}` }} />

            {drawText && <>
                <defs>{flip ?
                    <path id={textId} d={`
                        M ${textRightCorner.x} ${textRightCorner.y}
                        A ${textRadius} ${textRadius} 0 0 0 ${textLeftCorner.x} ${textLeftCorner.y}
                    `} /> :
                    <path id={textId} d={`
                        M ${textLeftCorner.x} ${textLeftCorner.y}
                        A ${textRadius} ${textRadius} 0 0 1 ${textRightCorner.x} ${textRightCorner.y} 
                    `} />
                }</defs>
                <text x={0} y={0}
                    textAnchor="middle"
                    dominantBaseline="middle"
                >
                    <textPath href={`#${textId}`}
                        startOffset="50%"
                    >
                        <tspan fontSize={"24px"} fontWeight={700}>{text}</tspan>
                        <tspan fontSize={"14px"}> {unit}</tspan>
                    </textPath>
                </text>
            </>}

            {label && <>
                <defs>{flip ?
                    <path id={id} d={`
                        M ${labelRightCorner.x} ${labelRightCorner.y}
                        A ${labelRadius} ${labelRadius} 0 0 0 ${labelLeftCorner.x} ${labelLeftCorner.y}
                    `} /> :
                    <path id={id} d={`
                        M ${labelLeftCorner.x} ${labelLeftCorner.y}
                        A ${labelRadius} ${labelRadius} 0 0 1 ${labelRightCorner.x} ${labelRightCorner.y} 
                    `} />
                }</defs>
                <text x={0} y={0}
                    textAnchor="middle"
                    dominantBaseline={flip ? "hanging" : "auto"}
                    style={labelStyle}
                >
                    <textPath href={`#${id}`}
                        startOffset="50%"
                    >
                        {label}
                    </textPath>
                </text>
            </>}
        </g >
    );
});

type BaseSection = {
    label: string;
    color: [from: ColorRepresentation, to: ColorRepresentation];
    size?: number;
    labelStyle?: CSSProperties;
}
type PercentSection = BaseSection & {
    type: "percent";
    value: number | SpringValue<number>;
}
type UnitSection = BaseSection & {
    type: "unit";
    value: number | SpringValue<number>;
    unit: string;
    min: number;
    max: number;
}
type BoolSection = BaseSection & {
    type: "bool";
    value: boolean | SpringValue<boolean>;
}
type Section = PercentSection | UnitSection | BoolSection;

type MetricProps = {
    size: number;
    width: number;
    sections: Section[];
    opacity?: number;
    scale?: number;
}

export function CircularDesignMetric({ size, width, sections, opacity = 1, scale = 1, ...props }: Omit<HtmlProps, "scale"> & MetricProps) {
    const offset = 40;

    const segments = useMemo(() => {
        let rotation = 180;
        let totalSize = 0;
        sections.every(section => totalSize += section.size ?? 1);
        let part = 360 / totalSize;

        return sections.map((section, index) => {
            let unit = "";
            switch (section.type) {
                case "percent":
                    unit = "%";
                    break;
                case "unit":
                    unit = section.unit;
                    break;
                case "bool":
                    break;
            }
            const angle = part * (section.size ?? 1);
            if (index > 0) rotation += angle * 0.5;
            const rot = rotation;
            rotation += angle * 0.5;
            return (
                <g key={section.label} transform={`translate(${offset * 0.5} ${offset * 0.5})`}>
                    <Segment margin={0.75}
                        rotation={rot} angle={angle}
                        size={width} totalSize={size - offset}
                        color={["#ffffff", "#ffffff"]}
                        value={1}
                    />
                    <Segment margin={0.75} drawText
                        rotation={rot} angle={angle}
                        size={width} totalSize={size - offset}
                        color={section.color}
                        label={section.label} labelOffset={5} labelStyle={section.labelStyle}
                        value={section.value} min={(section as UnitSection).min} max={(section as UnitSection).max}
                        unit={unit}
                    />
                </g>
            );
        });
    }, [sections, size, width, offset]);

    return (
        <Html center {...props} className={styles.root}>
            <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity, transform: `scale(${scale})` }}>
                {segments}
            </svg>
        </Html>
    );
}